import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { X, Loader2, RotateCcw, Save, Trash2, BookmarkPlus } from "lucide-react";

const FORMAT_DIMS = {
  post: { w: 1080, h: 1350, previewW: 320 },
  story: { w: 1080, h: 1920, previewW: 280 },
};

const DEFAULT_LAYOUTS = {
  post: {
    logo:      { x_pct: 0.056, y_pct: 0.044 },
    kicker:    { x_pct: 0.076, y_pct: 0.72 },
    title:     { x_pct: 0.076, y_pct: 0.77 },
    watermark: { x_pct: 0.175, y_pct: 0.4 },
  },
  story: {
    logo:      { x_pct: 0.065, y_pct: 0.104 },
    kicker:    { x_pct: 0.083, y_pct: 0.75 },
    title:     { x_pct: 0.083, y_pct: 0.80 },
    watermark: { x_pct: 0.175, y_pct: 0.4 },
  },
};

const ELEMENT_LABELS = {
  logo: "LOGO",
  kicker: "CATEGORÍA",
  title: "TÍTULO",
  watermark: "MARCA DE AGUA",
};

// Approximate handle sizes (visual only) as % of canvas — used to draw the drag handle
const HANDLE_SIZES = {
  post: {
    logo:      { w_pct: 0.22, h_pct: 0.043 },
    kicker:    { w_pct: 0.14, h_pct: 0.030 },
    title:     { w_pct: 0.85, h_pct: 0.20 },
    watermark: { w_pct: 0.65, h_pct: 0.065 },
  },
  story: {
    logo:      { w_pct: 0.22, h_pct: 0.030 },
    kicker:    { w_pct: 0.14, h_pct: 0.021 },
    title:     { w_pct: 0.83, h_pct: 0.18 },
    watermark: { w_pct: 0.65, h_pct: 0.046 },
  },
};

function DraggableTag({ id, label, xPct, yPct, wPct, hPct, active, onDrag, onSelect, containerRef }) {
  const dragging = useRef(false);
  const startMouse = useRef({ x: 0, y: 0 });
  const startPos = useRef({ xPct, yPct });

  const onMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragging.current = true;
    startMouse.current = { x: e.clientX, y: e.clientY };
    startPos.current = { xPct, yPct };
    onSelect?.(id);
    const move = (ev) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const dxPct = (ev.clientX - startMouse.current.x) / rect.width;
      const dyPct = (ev.clientY - startMouse.current.y) / rect.height;
      const nx = Math.max(0, Math.min(1 - wPct, startPos.current.xPct + dxPct));
      const ny = Math.max(0, Math.min(1 - hPct, startPos.current.yPct + dyPct));
      onDrag(id, { x_pct: nx, y_pct: ny });
    };
    const up = () => {
      dragging.current = false;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      onDrag(id, null, true); // signal drop
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  return (
    <div
      data-testid={`designer-tag-${id}`}
      onMouseDown={onMouseDown}
      style={{
        position: "absolute",
        left: `${xPct * 100}%`,
        top: `${yPct * 100}%`,
        width: `${wPct * 100}%`,
        height: `${hPct * 100}%`,
      }}
      className={`cursor-move select-none flex items-start justify-start ${active ? "ring-2 ring-turquoise bg-turquoise/25" : "bg-turquoise/10 hover:bg-turquoise/20 border border-turquoise/60"} border-dashed`}
    >
      <span className="text-[9px] uppercase tracking-widest font-bold text-white bg-turquoise/90 px-1.5 py-0.5 leading-tight">
        {label}
      </span>
    </div>
  );
}

export default function SocialImageDesigner({ article, onClose, onGenerated, initialTemplate = "surprimicia-clasica" }) {
  const [format, setFormat] = useState("post");
  const [template, setTemplate] = useState(initialTemplate);
  const [watermark, setWatermark] = useState(false);
  const [watermarkOpacity, setWatermarkOpacity] = useState(55); // 0-255
  const [watermarkScale, setWatermarkScale] = useState(0.65);   // 0.15-0.95
  const [logoScale, setLogoScale] = useState(null); // null = usar default del backend
  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState("");
  const [presetName, setPresetName] = useState("");
  const [presetIsDefault, setPresetIsDefault] = useState(false);
  const [savingPreset, setSavingPreset] = useState(false);
  const [titleOverride, setTitleOverride] = useState("");
  const [kickerOverride, setKickerOverride] = useState("");
  const [layout, setLayout] = useState({
    post: { ...DEFAULT_LAYOUTS.post },
    story: { ...DEFAULT_LAYOUTS.story },
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeEl, setActiveEl] = useState(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const seqRef = useRef(0);

  const dims = FORMAT_DIMS[format];
  const handleSizes = HANDLE_SIZES[format];
  const previewH = Math.round(dims.previewW * (dims.h / dims.w));

  const fetchPreview = useCallback(async () => {
    const mySeq = ++seqRef.current;
    setPreviewLoading(true);
    try {
      const blob = await api.adminSocialImagesPreview({
        article_id: article.id,
        format,
        template_id: template,
        watermark,
        watermark_opacity: watermark ? watermarkOpacity : undefined,
        watermark_scale: watermark ? watermarkScale : undefined,
        logo_scale: logoScale ?? undefined,
        title_override: titleOverride || undefined,
        kicker: kickerOverride || undefined,
        layout: layout[format],
      });
      if (mySeq !== seqRef.current) return; // discard stale
      const url = URL.createObjectURL(blob);
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (e) {
      toast.error("Error generando preview");
    } finally {
      if (mySeq === seqRef.current) setPreviewLoading(false);
    }
  }, [article.id, format, template, watermark, watermarkOpacity, watermarkScale, logoScale, titleOverride, kickerOverride, layout]);

  // Debounced re-fetch on any layout / setting change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchPreview, 350);
    return () => clearTimeout(debounceRef.current);
  }, [fetchPreview]);

  // Revoke object URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleDrag = (elId, coords, dropped) => {
    if (coords) {
      setLayout((prev) => ({
        ...prev,
        [format]: { ...prev[format], [elId]: coords },
      }));
    }
    // dropped=true → debounce will trigger fetchPreview automatically
  };

  const resetLayout = () => {
    setLayout({
      post: { ...DEFAULT_LAYOUTS.post },
      story: { ...DEFAULT_LAYOUTS.story },
    });
    toast.info("Posiciones restablecidas");
  };

  const saveAndGenerate = async () => {
    setSaving(true);
    try {
      // Generate BOTH formats using the format-specific layouts
      const results = [];
      for (const fmt of ["post", "story"]) {
        const res = await api.adminSocialImagesGenerate({
          article_id: article.id,
          formats: [fmt],
          template_id: template,
          title_override: titleOverride || null,
          kicker: kickerOverride || null,
          layout: layout[fmt],
          watermark,
          watermark_opacity: watermark ? watermarkOpacity : null,
          watermark_scale: watermark ? watermarkScale : null,
          logo_scale: logoScale,
        });
        results.push(...(res.outputs || []));
      }
      toast.success(`${results.length} imagen(es) generada(s) con el diseño personalizado`);
      onGenerated?.(results);
      onClose?.();
    } catch (e) {
      toast.error("Error guardando el diseño");
    } finally {
      setSaving(false);
    }
  };

  // Presets
  const refreshPresets = useCallback(async () => {
    try {
      const res = await api.adminSocialPresetsList();
      setPresets(res.items || []);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => { refreshPresets(); }, [refreshPresets]);

  const applyPreset = (id) => {
    setSelectedPresetId(id);
    if (!id) { setPresetIsDefault(false); return; }
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    setTemplate(p.template_id || "surprimicia-clasica");
    setWatermark(!!p.watermark);
    if (p.watermark_opacity != null) setWatermarkOpacity(p.watermark_opacity);
    if (p.watermark_scale != null) setWatermarkScale(p.watermark_scale);
    setLogoScale(p.logo_scale ?? null);
    setLayout({
      post: p.layout_post || DEFAULT_LAYOUTS.post,
      story: p.layout_story || DEFAULT_LAYOUTS.story,
    });
    setPresetName(p.name);
    setPresetIsDefault(!!p.is_default);
    toast.success(`Preset "${p.name}" aplicado`);
  };

  const savePreset = async () => {
    const name = (presetName || "").trim();
    if (!name) {
      toast.error("Poné un nombre al preset");
      return;
    }
    setSavingPreset(true);
    try {
      const saved = await api.adminSocialPresetSave({
        name,
        template_id: template,
        layout_post: layout.post,
        layout_story: layout.story,
        watermark,
        watermark_opacity: watermark ? watermarkOpacity : null,
        watermark_scale: watermark ? watermarkScale : null,
        logo_scale: logoScale,
        is_default: presetIsDefault,
      });
      toast.success(`Preset "${saved.name}" guardado${saved.is_default ? " (predeterminado)" : ""}`);
      await refreshPresets();
      setSelectedPresetId(saved.id);
    } catch (e) {
      toast.error("Error guardando el preset");
    } finally {
      setSavingPreset(false);
    }
  };

  const deletePreset = async () => {
    if (!selectedPresetId) return;
    const p = presets.find((x) => x.id === selectedPresetId);
    if (!p) return;
    if (!confirm(`¿Eliminar el preset "${p.name}"?`)) return;
    try {
      await api.adminSocialPresetDelete(selectedPresetId);
      toast.success("Preset eliminado");
      setSelectedPresetId("");
      setPresetName("");
      await refreshPresets();
    } catch {
      toast.error("Error eliminando el preset");
    }
  };

  const elementsToShow = useMemo(() => {
    const els = ["logo", "kicker", "title"];
    if (watermark) els.push("watermark");
    return els;
  }, [watermark]);

  // Effective logo scale for handle sizing (falls back to backend default)
  const DEFAULT_LOGO_FRAC = { post: 360 / 1080, story: 400 / 1080 };
  const effectiveLogoFrac = logoScale ?? DEFAULT_LOGO_FRAC[format];
  const dynamicHandleSizes = useMemo(() => {
    // Logo aspect ratio ≈ 1470/146 = 10:1 → height fraction = width * (146/1470) * (canvasW/canvasH)
    const aspect = 146 / 1470;
    const logoH = effectiveLogoFrac * aspect * (dims.w / dims.h);
    return {
      ...handleSizes,
      logo: { w_pct: effectiveLogoFrac, h_pct: Math.max(0.025, logoH) },
    };
  }, [handleSizes, effectiveLogoFrac, dims]);

  return (
    <div
      data-testid="social-image-designer"
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 overflow-auto"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-[1180px] w-full max-h-[95vh] overflow-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-softblue px-5 py-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-turquoise font-bold">Diseñador manual</div>
            <h2 className="font-serif text-xl font-black text-blueblack line-clamp-1">{article.title}</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="designer-close"
            className="w-9 h-9 flex items-center justify-center hover:bg-lightgray"
          >
            <X className="w-5 h-5 text-blueblack" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-5 p-5">
          {/* Preview + drag canvas */}
          <div className="flex flex-col items-center gap-3">
            {/* Format tabs */}
            <div className="flex items-center border border-softblue">
              {["post", "story"].map((f) => (
                <button
                  key={f}
                  data-testid={`designer-format-${f}`}
                  onClick={() => setFormat(f)}
                  className={`px-3 h-8 text-[10px] uppercase tracking-widest font-bold ${format === f ? "bg-nightblue text-white" : "bg-white text-nightblue hover:bg-lightgray"}`}
                >
                  {f === "post" ? "Post 4:5" : "Historia 9:16"}
                </button>
              ))}
            </div>

            {/* Preview */}
            <div
              ref={containerRef}
              className="relative bg-blueblack shadow-lg"
              style={{ width: `${dims.previewW}px`, height: `${previewH}px` }}
              data-testid="designer-canvas"
            >
              {previewUrl && (
                <img
                  src={previewUrl}
                  alt="preview"
                  className="absolute inset-0 w-full h-full object-cover"
                  draggable={false}
                />
              )}
              {previewLoading && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
              {elementsToShow.map((el) => (
                <DraggableTag
                  key={el}
                  id={el}
                  label={ELEMENT_LABELS[el]}
                  xPct={layout[format][el].x_pct}
                  yPct={layout[format][el].y_pct}
                  wPct={dynamicHandleSizes[el].w_pct}
                  hPct={dynamicHandleSizes[el].h_pct}
                  active={activeEl === el}
                  onDrag={handleDrag}
                  onSelect={setActiveEl}
                  containerRef={containerRef}
                />
              ))}
            </div>

            <div className="text-[11px] text-textgray text-center max-w-[320px]">
              Arrastrá los cuadros para mover cada elemento. El preview se actualiza al soltar.
            </div>
          </div>

          {/* Controls */}
          <div className="space-y-5 min-w-0">
            {/* Template */}
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-nightblue mb-1.5">Plantilla</div>
              <div className="flex items-center gap-1 border border-softblue w-fit">
                {[{ k: "surprimicia-clasica", label: "Clásica" }, { k: "breaking", label: "Breaking" }].map((t) => (
                  <button
                    key={t.k}
                    data-testid={`designer-template-${t.k}`}
                    onClick={() => setTemplate(t.k)}
                    className={`px-3 h-9 text-[11px] uppercase tracking-widest font-bold ${template === t.k ? "bg-nightblue text-white" : "bg-white text-nightblue hover:bg-lightgray"}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Overrides */}
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-nightblue">Título para la placa (opcional)</label>
              <input
                data-testid="designer-title-override"
                type="text"
                value={titleOverride}
                onChange={(e) => setTitleOverride(e.target.value)}
                placeholder={article.title}
                className="w-full h-9 px-2 mt-1 border border-softblue text-sm"
              />
              <div className="text-[11px] text-textgray mt-1">{titleOverride.length || article.title.length} caracteres</div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-nightblue">Kicker / Categoría (opcional)</label>
              <input
                data-testid="designer-kicker-override"
                type="text"
                value={kickerOverride}
                onChange={(e) => setKickerOverride(e.target.value)}
                placeholder={article.main_category_name || "Categoría"}
                className="w-full h-9 px-2 mt-1 border border-softblue text-sm"
              />
            </div>

            {/* Logo size */}
            <div className="border border-softblue p-3 bg-lightgray/50">
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] uppercase tracking-widest font-bold text-nightblue">Tamaño del logo</label>
                <span className="text-[11px] font-mono text-turquoise">{Math.round(effectiveLogoFrac * 100)}%</span>
              </div>
              <input
                data-testid="designer-logo-scale"
                type="range"
                min="15"
                max="60"
                value={Math.round(effectiveLogoFrac * 100)}
                onChange={(e) => setLogoScale(parseInt(e.target.value, 10) / 100)}
                className="w-full accent-turquoise"
              />
              <div className="flex items-center justify-between mt-1">
                <span className="text-[10px] text-textgray">Ancho respecto del canvas</span>
                <button
                  onClick={() => setLogoScale(null)}
                  className="text-[10px] uppercase tracking-widest text-nightblue hover:text-turquoise font-semibold"
                >
                  Restablecer
                </button>
              </div>
            </div>

            {/* Watermark */}
            <div className="border border-softblue p-3 bg-lightgray/50">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="designer-watermark-toggle"
                  checked={watermark}
                  onChange={(e) => setWatermark(e.target.checked)}
                  className="w-4 h-4 accent-turquoise"
                />
                <span className="text-sm font-semibold text-nightblue">Aplicar marca de agua (logo semi-transparente)</span>
              </label>
              {watermark && (
                <div className="mt-3 space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[10px] uppercase tracking-widest font-bold text-nightblue">Opacidad</label>
                      <span className="text-[11px] font-mono text-turquoise">{Math.round((watermarkOpacity / 255) * 100)}%</span>
                    </div>
                    <input
                      data-testid="designer-watermark-opacity"
                      type="range"
                      min="10"
                      max="255"
                      value={watermarkOpacity}
                      onChange={(e) => setWatermarkOpacity(parseInt(e.target.value, 10))}
                      className="w-full accent-turquoise"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[10px] uppercase tracking-widest font-bold text-nightblue">Tamaño</label>
                      <span className="text-[11px] font-mono text-turquoise">{Math.round(watermarkScale * 100)}%</span>
                    </div>
                    <input
                      data-testid="designer-watermark-scale"
                      type="range"
                      min="15"
                      max="95"
                      value={Math.round(watermarkScale * 100)}
                      onChange={(e) => setWatermarkScale(parseInt(e.target.value, 10) / 100)}
                      className="w-full accent-turquoise"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Preset library */}
            <div className="border border-softblue p-3 bg-white">
              <div className="text-[10px] uppercase tracking-widest font-bold text-nightblue mb-2">Diseños guardados</div>
              <div className="flex items-center gap-2 mb-2">
                <select
                  data-testid="designer-preset-select"
                  value={selectedPresetId}
                  onChange={(e) => applyPreset(e.target.value)}
                  className="flex-1 h-9 px-2 border border-softblue text-sm bg-white"
                >
                  <option value="">— Cargar un preset —</option>
                  {presets.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.is_default ? "★ " : ""}{p.name}
                    </option>
                  ))}
                </select>
                {selectedPresetId && (
                  <button
                    data-testid="designer-preset-delete"
                    onClick={deletePreset}
                    className="h-9 w-9 flex items-center justify-center border border-softblue text-red-600 hover:bg-red-50"
                    title="Eliminar este preset"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  data-testid="designer-preset-name"
                  type="text"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder="Nombre del preset (ej. Placa política)"
                  className="flex-1 h-9 px-2 border border-softblue text-sm"
                />
                <Button
                  data-testid="designer-preset-save"
                  onClick={savePreset}
                  disabled={savingPreset || !presetName.trim()}
                  className="h-9 bg-nightblue hover:bg-blueblack text-white rounded-none text-[11px] uppercase tracking-widest font-bold px-3"
                >
                  {savingPreset ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><BookmarkPlus className="w-3.5 h-3.5 mr-1" /> Guardar</>}
                </Button>
              </div>
              <label className="flex items-center gap-2 cursor-pointer mt-2">
                <input
                  type="checkbox"
                  data-testid="designer-preset-default"
                  checked={presetIsDefault}
                  onChange={(e) => setPresetIsDefault(e.target.checked)}
                  className="w-4 h-4 accent-turquoise"
                />
                <span className="text-[11px] font-semibold text-nightblue">★ Marcar como predeterminado (aplicar auto a cada nueva placa)</span>
              </label>
              <div className="text-[10px] text-textgray mt-1.5">
                Guarda posiciones, plantilla y marca de agua. Reutilizable en futuras notas.
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-wrap pt-4 border-t border-softblue">
              <Button
                data-testid="designer-reset"
                onClick={resetLayout}
                variant="outline"
                className="h-9 border-softblue text-nightblue rounded-none text-[11px] uppercase tracking-widest font-semibold"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restablecer posiciones
              </Button>
              <Button
                data-testid="designer-save"
                onClick={saveAndGenerate}
                disabled={saving}
                className="h-9 bg-turquoise hover:bg-nightblue text-white rounded-none text-[11px] uppercase tracking-widest font-bold"
              >
                {saving ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generando…</>
                ) : (
                  <><Save className="w-3.5 h-3.5 mr-1.5" /> Guardar y generar (Post + Story)</>
                )}
              </Button>
            </div>

            <div className="text-[11px] text-textgray leading-relaxed bg-lightgray p-2">
              <b>Tip:</b> las posiciones se guardan por formato. Podés diseñar Post e Historia por separado.
              La marca de agua usa el logo de Sur Primicia y se pinta detrás de textos y logo.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
